import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Anchor, FileCheck2, Handshake, Bell, Search, ArrowRight, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BoatCard from '../components/boats/BoatCard';
import BoatFilters, { EMPTY_FILTERS } from '../components/boats/BoatFilters';
import { BoatAlertModal } from '../components/boats/BoatAlert';
import { CompareBar } from '../components/boats/BoatTools';
import { API_URL, getFavorites, toggleFavorite, boatEvent, track } from '../lib/boats';

const SITE = 'https://validatedboat.fr';
const btnPrimary = 'inline-flex items-center justify-center gap-2 bg-[#d9fb06] text-[#1a1c1b] rounded-full px-6 py-3 font-bold uppercase text-sm hover:bg-[#c4e205] hover:scale-105 transition-all';
const btnGhost = 'inline-flex items-center justify-center gap-2 border-2 border-[#d9fb06] text-[#d9fb06] rounded-full px-6 py-3 font-bold uppercase text-sm hover:bg-[#d9fb06] hover:text-[#1a1c1b] transition-all';

const Section = ({ title, subtitle, children, id, testId }) => (
  <section id={id} className="space-y-6" data-testid={testId}>
    <div><h2 className="text-2xl md:text-3xl font-black text-[#dfddd6] uppercase">{title}</h2>{subtitle && <p className="text-[#888680] mt-1">{subtitle}</p>}</div>
    {children}
  </section>
);

export default function BoatsCatalog() {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [boats, setBoats] = useState([]);
  const [sold, setSold] = useState([]);
  const [totalPublic, setTotalPublic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favs, setFavs] = useState(getFavorites());
  const [alertOpen, setAlertOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v !== ''));
    const ctrl = new AbortController();
    setLoading(true);
    fetch(`${API_URL}/api/boats?${params}`, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((d) => { setBoats(d.boats || []); setTotalPublic(d.total_public ?? 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [filters]);

  useEffect(() => {
    fetch(`${API_URL}/api/boats?sold=1`).then((r) => r.json()).then((d) => setSold(d.boats || [])).catch(() => {});
  }, []);

  const hasActiveFilters = Object.entries(filters).some(([k, v]) => k !== 'sort' && v !== '');
  const news = useMemo(() => boats.filter((b) => b.published_at && Date.now() - new Date(b.published_at).getTime() < 30 * 86400000).slice(0, 6), [boats]);
  const featured = useMemo(() => boats.filter((b) => b.featured), [boats]);
  const onFav = (id) => { const next = toggleFavorite(id); setFavs(next); if (next.includes(id)) { boatEvent(id, 'favorite'); track('boat_favorite_added', { boat_id: id }); } };
  const cardProps = (b) => ({ boat: b, isFav: favs.includes(b.id), onFav });
  const empty = totalPublic === 0;

  const schema = {
    '@context': 'https://schema.org', '@type': 'CollectionPage', name: 'Bateaux à vendre sélectionnés par ValidatedBoat', url: `${SITE}/bateaux-a-vendre`,
    breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE }, { '@type': 'ListItem', position: 2, name: 'Bateaux à vendre', item: `${SITE}/bateaux-a-vendre` }] },
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Helmet>
        <title>Bateaux à vendre sélectionnés — Voiliers et bateaux à moteur d'occasion | ValidatedBoat La Rochelle</title>
        <meta name="description" content="Bateaux à vendre présentés avec méthode et transparence : historique connu, informations vérifiées ou déclarées, points à contrôler et accompagnement indépendant par Nicolas Daval, conseiller nautique à La Rochelle." />
        <link rel="canonical" href={`${SITE}/bateaux-a-vendre`} />
        <meta name="robots" content={hasActiveFilters ? 'noindex, follow' : 'index, follow'} />
        <meta property="og:title" content="Bateaux à vendre sélectionnés | ValidatedBoat" />
        <meta property="og:description" content="Une sélection de bateaux mieux documentés, mieux présentés et mieux compris." />
        <meta property="og:url" content={`${SITE}/bateaux-a-vendre`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.pexels.com/photos/1295036/pexels-photo-1295036.jpeg?auto=compress&cs=tinysrgb&w=1200" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <Header />

      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden" data-testid="boats-hero">
        <img src="https://images.pexels.com/photos/1295036/pexels-photo-1295036.jpeg?auto=compress&cs=tinysrgb&w=1920" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b]/60 via-[#1a1c1b]/80 to-[#1a1c1b]" />
        <div className="relative max-w-[87.5rem] mx-auto px-5 md:px-10">
          <nav aria-label="Fil d'Ariane" className="text-xs text-[#888680] mb-6"><Link to="/" className="hover:text-[#d9fb06]">Accueil</Link> <span aria-hidden="true">/</span> <span className="text-[#dfddd6]">Bateaux à vendre</span></nav>
          <div className="max-w-3xl">
            <p className="text-[#d9fb06] font-bold uppercase tracking-widest text-xs mb-4">Bateaux sélectionnés</p>
            <h1 className="font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#d9fb06] uppercase mb-6">Des bateaux à vendre. Des informations pour vraiment choisir.</h1>
            <p className="text-base md:text-lg text-[#dfddd6] leading-relaxed mb-8">Découvrez une sélection de bateaux présentés avec méthode, transparence et regard professionnel. Chaque annonce vous aide à comprendre les qualités du bateau, son historique connu, son positionnement sur le marché et les points qui méritent une vérification avant de vous déplacer.</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <a href="#catalogue" className={btnPrimary} data-testid="hero-discover-btn">Découvrir les bateaux</a>
              <Link to="/vendre-mon-bateau" className={btnGhost} data-testid="hero-sell-btn">Confier la vente de mon bateau</Link>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[[Anchor, 'Bateaux sélectionnés'], [FileCheck2, 'Informations clairement identifiées'], [Handshake, 'Accompagnement indépendant']].map(([Icon, t]) => (
                <li key={t} className="flex items-center gap-3 text-sm text-[#dfddd6]"><span className="w-10 h-10 rounded-full bg-[#d9fb06]/10 border border-[#d9fb06]/40 flex items-center justify-center flex-shrink-0"><Icon size={18} className="text-[#d9fb06]" /></span>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <main className="max-w-[87.5rem] mx-auto px-5 md:px-10 pb-24 space-y-16" id="catalogue">
        {empty && !loading ? (
          <section className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-8 md:p-14 text-center" data-testid="boats-empty-state">
            <Anchor className="mx-auto text-[#d9fb06] mb-5" size={44} />
            <h2 className="text-2xl md:text-3xl font-black text-[#dfddd6] uppercase mb-3">Les premiers bateaux sélectionnés par ValidatedBoat seront bientôt disponibles.</h2>
            <p className="text-[#888680] max-w-2xl mx-auto mb-8">Vous recherchez un bateau précis ou souhaitez confier le vôtre à la vente ? Faites-nous part de votre projet dès maintenant.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/services/recherche-bateau" className={btnPrimary} data-testid="empty-search-btn"><Search size={16} /> Confier ma recherche</Link>
              <Link to="/vendre-mon-bateau" className={btnGhost} data-testid="empty-estimate-btn">Faire estimer mon bateau</Link>
              <button type="button" onClick={() => setAlertOpen(true)} className={btnGhost} data-testid="empty-alert-btn"><Bell size={16} /> Créer une alerte</button>
            </div>
          </section>
        ) : (
          <>
            <BoatFilters filters={filters} onChange={setFilters} count={boats.length} />
            {loading ? (
              <div className="py-16 text-center text-[#888680]"><Loader2 className="animate-spin inline mr-2" /> Chargement des bateaux…</div>
            ) : boats.length === 0 ? (
              <section className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-8 md:p-12 text-center" data-testid="boats-no-results">
                <h2 className="text-xl md:text-2xl font-black text-[#dfddd6] uppercase mb-3">Aucun bateau ne correspond exactement à votre projet ?</h2>
                <p className="text-[#888680] mb-6">Confiez votre recherche à ValidatedBoat, ou soyez prévenu dès qu'un bateau correspondant est publié.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button type="button" onClick={() => setAlertOpen(true)} className={btnPrimary} data-testid="noresult-alert-btn"><Bell size={16} /> Créer une alerte</button>
                  <Link to="/services/recherche-bateau" className={btnGhost} data-testid="noresult-search-btn">Confier ma recherche</Link>
                </div>
              </section>
            ) : (
              <>
                {!hasActiveFilters && news.length > 0 && <Section title="Les nouveautés" subtitle="Publiés au cours des 30 derniers jours" testId="section-news"><div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{news.map((b) => <BoatCard key={b.id} {...cardProps(b)} />)}</div></Section>}
                {!hasActiveFilters && featured.length > 0 && <Section title="Le choix de ValidatedBoat" subtitle="Des bateaux dont la cohérence prix / état / documentation a particulièrement retenu mon attention" testId="section-featured"><div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{featured.map((b) => <BoatCard key={b.id} {...cardProps(b)} />)}</div></Section>}
                <Section title={hasActiveFilters ? 'Résultats de votre recherche' : 'Tous les bateaux disponibles'} testId="section-all"><div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{boats.map((b) => <BoatCard key={b.id} {...cardProps(b)} />)}</div></Section>
              </>
            )}
            {sold.length > 0 && (
              <Section title="Bateaux récemment vendus" subtitle="Des projets menés à bien avec leurs propriétaires" testId="section-sold">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 opacity-90">{sold.slice(0, 6).map((b) => <BoatCard key={b.id} boat={b} />)}</div>
              </Section>
            )}
          </>
        )}

        <section className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-8 md:p-12 grid lg:grid-cols-5 gap-8 items-center" data-testid="boats-conversion">
          <div className="lg:col-span-3">
            <p className="text-[#d9fb06] font-bold text-sm mb-3">ValidatedBoat ne vous montre pas seulement des bateaux. Il vous aide à comprendre ce que vous achetez.</p>
            <h2 className="text-2xl md:text-3xl font-black text-[#dfddd6] uppercase mb-3">Vous n'avez pas encore trouvé le bon bateau ?</h2>
            <p className="text-[#888680]">Décrivez votre projet, votre budget et votre programme de navigation. ValidatedBoat recherche, analyse et compare les bateaux susceptibles de réellement vous correspondre.</p>
          </div>
          <div className="lg:col-span-2 flex flex-col gap-3">
            <Link to="/services/recherche-bateau" className={btnPrimary} data-testid="bottom-search-btn">Confier ma recherche <ArrowRight size={16} /></Link>
            <Link to="/#contact" className={btnGhost} data-testid="bottom-call-btn">Réserver un appel découverte</Link>
            {!empty && <button type="button" onClick={() => setAlertOpen(true)} className="text-sm text-[#888680] underline hover:text-[#d9fb06]" data-testid="bottom-alert-btn">Créer une alerte pour ma recherche</button>}
          </div>
        </section>
      </main>
      <BoatAlertModal open={alertOpen} onClose={() => setAlertOpen(false)} />
      <CompareBar />
      <Footer />
    </div>
  );
}
